import React, { memo } from 'react';
import { Pressable, StyleProp, ViewStyle } from 'react-native';

import { Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

type BookClassButtonProps = {
  onPress?: () => void;
  variant?: 'solid' | 'dashed';
  style?: StyleProp<ViewStyle>;
};

const strings = STRINGS.guardian.home.upcomingWeek;

function BookClassButton({ onPress, variant = 'dashed', style }: BookClassButtonProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const isDashed = variant === 'dashed';

  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={strings.bookClass}
      onPress={onPress}
      style={({ pressed }) => [
        styles.root,
        isDashed ? styles.dashedRoot : styles.solidRoot,
        pressed ? styles.pressed : null,
        style,
      ]}>
      <Stack
        align="center"
        justify="center"
        style={[styles.iconWrapper, isDashed && styles.dashedIconWrapper]}>
        <SvgIcon name="IcPlusBold" size={scale(20)} color={guardianColors['Main/Main']} />
      </Stack>
      <Text variant="Font Guardian App/Subtitle/Subtitle 12 Semibold" style={styles.label}>
        {strings.bookClass}
      </Text>
    </Pressable>
  );
}

const useStyles = createStyles(({ scale }) => ({
  root: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  pressed: {
    opacity: 0.72,
  },
  dashedRoot: {
    paddingHorizontal: scale(24),
    paddingVertical: scale(8),
    borderRadius: scale(8),
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: guardianColors.Blue,
    backgroundColor: colors['0'],
  },
  solidRoot: {
    paddingHorizontal: scale(24),
    paddingVertical: scale(8),
    borderRadius: scale(12),
    borderWidth: 0.5,
    borderColor: guardianColors.Blue,
    backgroundColor: colors['0'],
  },
  iconWrapper: {
    marginRight: scale(8),
  },
  dashedIconWrapper: {
    width: scale(32),
    height: scale(32),
    borderRadius: scale(16),
    backgroundColor: guardianColors['Main/10'],
  },
  label: {
    color: guardianColors['Main/Main'],
  },
}));

export default memo(BookClassButton);
